import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BookOpen,
  CircleUserRound,
  Info,
  FileText,
  Home,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  BarChart3,
  Settings,
  Tag,
  Users,
  MessageSquare,
  Scale,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import icon from '../assets/icon.png';
import { useAppSetting } from '../context/AppSettingContext';

function Sidebar({ role, user }) {
  const { appSetting } = useAppSetting();
  const navigate = useNavigate();
  const location = useLocation();

  const [plan, setPlan] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (role !== 'USER') return;

    const fetchPlan = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/current-plan`, {
          withCredentials: true,
        });
        setPlan(res.data.data || null);
      } catch {
        setPlan(null);
      }
    };

    fetchPlan();
  }, [role, location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 1024) setCollapsed(true);
      else setCollapsed(false);
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/logout`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch {
      // noop
    }

    navigate('/', { replace: true });
    window.location.reload();
  };

  const onClickLogout = () => {
    if (role === 'ADMIN') {
      setShowLogoutConfirm(true);
      return;
    }
    handleLogout();
  };

  const isActive = (path) => location.pathname === path;

  const adminMenus = useMemo(
    () => [
      { label: 'Dashboard', to: '/dashboard', icon: Home },
      { label: 'Pengaturan', to: '/dashboard/manage/setting', icon: Settings },
      { label: 'History Chat', to: '/dashboard/manage/chat', icon: MessageSquare },
      { label: 'Data Pengguna', to: '/dashboard/manage/users', icon: Users },
      { label: 'Manage Pricelist', to: '/dashboard/manage/pricelist', icon: Tag },
      { label: 'Manage Dokumen', to: '/dashboard/manage/document', icon: FileText },
      { label: 'Llama API Key', to: '/dashboard/manage/llama-api-key', icon: Tag },
      { label: 'Log Ingest', to: '/dashboard/manage/log-ingest', icon: FileText },
      { label: 'Evaluasi RAGAS', to: '/dashboard/manage/ragas-evaluation', icon: BarChart3 },
      { label: 'Transaction Order', to: '/dashboard/manage/transaction', icon: Receipt },
    ],
    []
  );

  const userMenus = useMemo(
    () => [
      { label: 'Dashboard', to: '/dashboard', icon: Home },
      { label: 'Chat', to: '/dashboard/chat', icon: MessageSquare },
      { label: 'Examination Assistant', to: '/dashboard/examination-assistant', icon: Scale },
      { label: 'Pricelist', to: '/dashboard/pricelist', icon: Tag },
      { label: 'Profil', to: '/dashboard/profile', icon: CircleUserRound },
    ],
    []
  );

  const menus = role === 'ADMIN' ? adminMenus : userMenus;

  return (
    <aside
      className="tw flex h-screen flex-col border-r border-slate-200 bg-white shadow-sm"
      style={{ width: collapsed ? 80 : 270 }}
    >
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-3">
        <div className="flex min-w-0 items-center gap-2">
          <img src={appSetting.logo_image ?? icon} alt="Logo" className="h-9 w-9 rounded-lg object-cover" />
          {!collapsed ? <span className="truncate text-sm font-semibold text-slate-800">{appSetting.nama_aplikasi ?? '-'}</span> : null}
        </div>

        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          className="rounded-lg border border-slate-200 bg-slate-50 p-1.5 text-slate-600 hover:bg-slate-100"
        >
          {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
        </button>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto p-2">
        {menus.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to);

          return (
            <Link
              key={item.to}
              to={item.to}
              className={`mb-1 flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition ${
                active
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={16} />
              {!collapsed ? <span>{item.label}</span> : null}
            </Link>
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-slate-200 p-3">
        <Link
          to="/dashboard/profile"
          className="mb-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2 hover:bg-slate-100"
        >
          {user?.photo_url ? (
            <img
              src={user.photo_url}
              alt="User Profile"
              className="h-9 w-9 rounded-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <CircleUserRound size={34} className="text-slate-500" />
          )}

          {!collapsed ? (
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-800">{user.name}</div>
              <div className="truncate text-xs text-slate-500">
                {role === 'USER' ? plan?.current_plan || 'FREE' : 'ADMIN'}
              </div>
            </div>
          ) : null}
        </Link>

        {!collapsed && role === 'USER' && plan ? (
          <div className="mb-2 rounded-xl border border-slate-200 bg-white p-2 text-[11px] text-slate-600">
            <PlanRow label="Session" value={`${plan.user_session ?? '-'} / ${plan.max_session === -1 ? 'Unlimited' : plan.max_session ?? '-'}`} />
            <PlanRow label="Chat" value={`${plan.user_attempt ?? '-'} / ${plan.max_attempt === -1 ? 'Unlimited' : plan.max_attempt ?? '-'}`} />
            <PlanRow label="Voice" value={`${plan.user_voice_attempt ?? '-'} / ${plan.max_voice_attempt === -1 ? 'Unlimited' : plan.max_voice_attempt ?? '-'}`} />
            <PlanRow label="Exam" value={`${plan.attempt_exam_assist ?? '-'} / ${plan.max_exam_assist === -1 ? 'Unlimited' : plan.max_exam_assist ?? '-'}`} />
          </div>
        ) : null}

        <button
          type="button"
          onClick={onClickLogout}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <LogOut size={15} />
          {!collapsed ? 'Logout' : null}
        </button>

        {!collapsed ? (
          <div className="mt-3 text-center text-[11px] text-slate-400">
            <div>Version 1.0.0</div>
            <div>© 2025 ChatLawID</div>
          </div>
        ) : null}
      </div>

      {showLogoutConfirm ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
            <div className="mb-3 flex items-start gap-2">
              <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
                <Info size={16} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-800">Konfirmasi Logout</h4>
                <p className="mt-1 text-xs text-slate-500">Anda yakin ingin keluar dari halaman admin?</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-lg border border-slate-900 bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                Ya, Logout
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

function PlanRow({ label, value }) {
  return (
    <div className="mb-1 flex items-center justify-between gap-2 last:mb-0">
      <span>{label}</span>
      <span className="font-semibold text-slate-700">{value}</span>
    </div>
  );
}

export default Sidebar;
